<script module lang="ts">
	import type { TxtVariant } from '../Txt';

	export type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'ghost';
	export type ButtonSize = 'xl' | 'lg' | 'md' | 'sm' | 'xs';

	export interface ButtonProps {
		class?: string;
		type?: 'button' | 'submit' | 'reset' | null | undefined;
		dataCy: string;
		variant?: ButtonVariant;
		size?: ButtonSize;
		role?: string;
		disabled?: boolean | null | undefined;
		loading?: boolean;
		fullWidth?: boolean;
		icon?: boolean;
		classes?: { label?: string };
		'aria-haspopup'?: import('svelte/elements').HTMLButtonAttributes['aria-haspopup'];
		'aria-expanded'?: import('svelte/elements').HTMLButtonAttributes['aria-expanded'];
		'aria-pressed'?: import('svelte/elements').HTMLButtonAttributes['aria-pressed'];
		'aria-label'?: import('svelte/elements').HTMLButtonAttributes['aria-label'];
		// Icon-only buttons need a tooltip and an accessible name; rest props
		// already reach the element, this just admits the attribute to the type.
		title?: import('svelte/elements').HTMLButtonAttributes['title'];
		children?: import('svelte').Snippet;
		leading?: import('svelte').Snippet;
		trailing?: import('svelte').Snippet;
		onclick?: ((e?: Event) => void) | undefined | null;
		onmouseup?: (
			e: MouseEvent & {
				currentTarget: EventTarget & HTMLButtonElement;
			}
		) => void;
	}

	export const ButtonVariantsMap: Readonly<Record<ButtonVariant, string>> = Object.freeze({
		primary:
			'icon-primary relative flex items-center border border-gray-900 bg-gray-900 text-gray-50 hover:border-gray-800 hover:bg-gray-800 active:bg-gray-700 focus:gray-900 focus:border-gray-800',
		secondary:
			'relative flex items-center bg-gray-0 border border-gray-200 hover:border-gray-700 active:border-gray-500 focus:border-gray-500',
		tertiary: 'relative flex items-center hover:bg-gray-100 active:bg-gray-200 focus:bg-gray-200',
		ghost:
			'icon-ghost relative flex items-center shadow-sm text-gray-50 border border-gray-200 hover:bg-[black]/40 hover:border-gray-200 hover:text-gray-0 active:bg-[black]/60 focus:border-gray-200'
	});

	export const DisabledMap: Readonly<Record<ButtonVariant, string>> = Object.freeze({
		primary: 'disabled:bg-gray-100 disabled:border-gray-100 disabled:text-gray-900/50',
		secondary: 'disabled:bg-gray-100 disabled:border-gray-100 disabled:text-gray-900/50',
		tertiary: 'disabled:bg-gray-100 disabled:border-gray-100 disabled:text-gray-900/50',
		ghost:
			'disabled:bg-gray-100 disabled:border-gray-100 disabled:text-gray-900/50 disabled:shadow-none'
	});

	export const ButtonLoadingMap: Readonly<Record<ButtonVariant, string>> = Object.freeze({
		primary: 'hover:bg-gray-900 active:bg-gray-900',
		secondary: 'hover:border-gray-200 active:border-gray-200',
		tertiary: '',
		ghost: 'bg-gray-900 hover:bg-gray-900/100 active:bg-gray-900/100 border-0'
	});

	export const ButtonSizesMap: Readonly<Record<ButtonSize, string>> = Object.freeze({
		xl: 'icon-xl px-[1rem] h-[3.5rem] text-center rounded uppercase',
		lg: 'icon-lg px-[0.875rem] h-[3rem] text-center rounded uppercase',
		md: 'icon-md px-[0.625rem] h-[2.5rem] text-center rounded uppercase',
		sm: 'icon-sm px-[0.5rem] h-[2rem] text-center rounded uppercase',
		xs: 'icon-xs px-[0.25rem] h-[1.5rem] text-center rounded uppercase'
	});

	export const ButtonVariants = Object.keys(ButtonVariantsMap) as ButtonVariant[];
	export const ButtonSizes = Object.keys(ButtonSizesMap) as ButtonSize[];

	export const TxtMap: Readonly<Record<ButtonSize, TxtVariant>> = Object.freeze({
		xl: 'label1',
		lg: 'label2',
		md: 'label3',
		sm: 'label4',
		xs: 'label4'
	});

	export const TxtClassesMap: Readonly<Record<ButtonSize, string>> = Object.freeze({
		xl: 'px-2',
		lg: 'px-2',
		md: 'px-1',
		sm: 'px-1',
		xs: 'px-0.5'
	});
</script>

<script lang="ts">
	import classNames from 'classnames';
	import { twMerge } from 'tailwind-merge';
	import { CircularProgress, type CircularProgressProps } from '../CircularProgress';
	import { Txt } from '../Txt';

	let {
		dataCy,
		variant = 'primary',
		size = 'xl',
		disabled = false,
		loading = false,
		icon = false,
		fullWidth = false,
		classes = {},
		children,
		trailing,
		leading,
		onclick,
		onmouseup,
		...rest
	}: ButtonProps = $props();

	let dataCyPrefix = $derived(`${dataCy}-button`);

	let variantClassNames = $derived(
		twMerge(ButtonVariantsMap[variant], classNames({ [ButtonLoadingMap[variant]]: loading }))
	);

	let sizeClassNames = $derived(ButtonSizesMap[size]);

	let buttonClasses = $derived(
		twMerge(
			'flex justify-center items-center transition-all',
			sizeClassNames,
			variantClassNames,
			classNames({ [DisabledMap[variant]]: disabled && !loading }),
			classNames(sizeClassNames, variantClassNames, {
				'icon-disabled': disabled
			}),
			classNames({ 'w-full': fullWidth }),
			rest.class
		)
	);

	let labelClasses = $derived(
		twMerge(
			classNames('tracking-widest whitespace-nowrap leading-[normal]', {
				'text-gray-50': variant === 'primary' && !disabled,
				'text-gray-0': variant === 'ghost',
				'text-gray-900/50': disabled,
				invisible: loading,
				'px-1.5': !trailing && !leading && !icon
			}),
			classes.label
		)
	);

	let loadingColor = $derived(
		(['primary', 'ghost'].includes(variant) ? 'light' : 'dark') as CircularProgressProps['color']
	);

	let loadingClasses = $derived(classNames('empty:hidden', { invisible: loading }));
</script>

<button
	{...rest}
	{onclick}
	{onmouseup}
	data-cy={dataCyPrefix}
	class={buttonClasses}
	disabled={disabled || loading}
>
	{#if loading}
		<span class="absolute left-0 right-0 top-0 bottom-0 h-[16px] w-[16px] m-auto">
			<CircularProgress dataCy={dataCyPrefix} color={loadingColor} />
		</span>
	{/if}

	<span class={loadingClasses}>
		{@render leading?.()}
	</span>

	{#if children}
		<Txt component="span" variant={TxtMap[size]} class={twMerge(TxtClassesMap[size], labelClasses)}>
			{@render children()}
		</Txt>
	{/if}

	<span class={loadingClasses}>
		{@render trailing?.()}
	</span>
</button>

<style>
	:global {
		.icon-primary svg path {
			fill: white;
		}

		.icon-ghost svg path {
			fill: white;
		}

		.icon-disabled svg path {
			fill: #222222;
			opacity: 0.8;
		}

		.icon-xl svg {
			width: 1.5rem;
			height: 1.5rem;
		}

		.icon-lg svg {
			width: 1.25rem;
			height: 1.25rem;
		}

		.icon-md svg {
			width: 1.25rem;
			height: 1.25rem;
		}

		.icon-sm svg {
			width: 1rem;
			height: 1rem;
		}

		.icon-xs svg {
			width: 0.75rem;
			height: 0.75rem;
		}
	}
</style>
